import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RotateCcw } from "lucide-react";
import EntityCard from "./EntityCard";
import { tables, relations, clusterColors } from "./schema-data";

// Positions for each table in the diagram (x, y percentages)
const tablePositions: Record<string, { x: number; y: number }> = {
  // Auth & Org (left column)
  users: { x: 5, y: 5 },
  user_roles: { x: 5, y: 22 },
  collaborateurs: { x: 5, y: 39 },
  services: { x: 5, y: 56 },
  // Coeur AO (center)
  appels_offres: { x: 35, y: 5 },
  depots: { x: 35, y: 25 },
  analyses: { x: 35, y: 45 },
  evaluations: { x: 35, y: 65 },
  criteres_evaluation: { x: 35, y: 83 },
  // Societes (right column, top)
  societes: { x: 65, y: 5 },
  candidats: { x: 65, y: 25 },
  societes_files: { x: 65, y: 45 },
  // Config (right column, bottom)
  prompt_templates: { x: 65, y: 63 },
  ao_access_permissions: { x: 5, y: 75 },
  ao_societe_notes: { x: 65, y: 83 },
};

export default function DataModelTab() {
  const [selectedTable, setSelectedTable] = useState<string | null>(null);

  const relatedTables = useMemo(() => {
    if (!selectedTable) return new Set<string>();
    const related = new Set<string>();
    related.add(selectedTable);
    relations.forEach((r) => {
      if (r.from === selectedTable) related.add(r.to);
      if (r.to === selectedTable) related.add(r.from);
    });
    return related;
  }, [selectedTable]);

  const activeRelations = useMemo(() => {
    if (!selectedTable) return relations;
    return relations.filter(
      (r) => r.from === selectedTable || r.to === selectedTable
    );
  }, [selectedTable]);

  const handleReset = useCallback(() => setSelectedTable(null), []);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-3">
          {Object.entries(clusterColors).map(([key, c]) => (
            <div key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={`h-2.5 w-2.5 rounded-sm border ${c.border} ${c.bg}`} />
              {c.label}
            </div>
          ))}
        </div>
        {selectedTable && (
          <Button variant="ghost" size="sm" onClick={handleReset} className="gap-1.5 text-xs">
            <RotateCcw className="h-3 w-3" />
            Reset
          </Button>
        )}
      </div>

      {/* ER Diagram */}
      <div className="relative border rounded-lg bg-muted/20 overflow-x-auto" style={{ minHeight: 700 }}>
        {/* SVG relation lines */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ minHeight: 700 }}
        >
          {activeRelations.map((rel) => {
            const fromPos = tablePositions[rel.from];
            const toPos = tablePositions[rel.to];
            if (!fromPos || !toPos) return null;

            // Card dimensions in px (approx 176px wide, 52px tall)
            const cardW = 176;
            const cardH = 52;
            // Container assumed 900px wide, 700px tall for the percentage calc
            const cW = 900;
            const cH = 700;

            const x1 = (fromPos.x / 100) * cW + cardW / 2;
            const y1 = (fromPos.y / 100) * cH + cardH / 2;
            const x2 = (toPos.x / 100) * cW + cardW / 2;
            const y2 = (toPos.y / 100) * cH + cardH / 2;

            const isHighlighted = selectedTable
              ? rel.from === selectedTable || rel.to === selectedTable
              : false;

            return (
              <line
                key={`${rel.from}-${rel.fromCol}-${rel.to}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={
                  isHighlighted
                    ? "hsl(var(--primary))"
                    : "hsl(var(--border))"
                }
                strokeWidth={isHighlighted ? 2 : 1}
                strokeDasharray={isHighlighted ? undefined : "4 4"}
                opacity={selectedTable ? (isHighlighted ? 1 : 0.15) : 0.4}
              />
            );
          })}
        </svg>

        {/* Entity cards */}
        {tables.map((table) => {
          const pos = tablePositions[table.name];
          if (!pos) return null;

          const isHighlighted = selectedTable
            ? relatedTables.has(table.name)
            : false;
          const isDimmed = selectedTable
            ? !relatedTables.has(table.name)
            : false;

          return (
            <div
              key={table.name}
              className="absolute"
              style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            >
              <EntityCard
                table={table}
                isHighlighted={isHighlighted}
                isDimmed={isDimmed}
                onClick={() =>
                  setSelectedTable(
                    selectedTable === table.name ? null : table.name
                  )
                }
              />
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <Badge variant="outline">{tables.length} tables</Badge>
        <Badge variant="outline">{relations.length} relations FK</Badge>
        <Badge variant="outline">4 enums PostgreSQL</Badge>
        <span className="text-muted-foreground/60">
          Cliquez sur une table pour voir ses relations
        </span>
      </div>
    </div>
  );
}
